import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, Filter, X, Loader2, ChevronLeft, ChevronRight, 
  LayoutGrid, Grid3X3, List, Plus, Images
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Toggle } from '../components/ui/toggle';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { getProxyImageUrl, normalizeCard } from '../utils';
import { LanguageBadge } from '../components/LanguageSelector';
import { SUPPORTED_LANGUAGES, getSupportedLanguageCodes } from '../constants/languageConfig';

// Constants
const CARDS_PER_PAGE = 30; // 6 rows of 5 cards
const LANGUAGES = getSupportedLanguageCodes();

// Helper to extract rarity code (e.g., 'RR' from '550_rr' or 'AP' from '350_ap')
const formatRarity = (rarity) => {
  if (!rarity) return null;
  const value = rarity?.value || rarity;
  if (typeof value !== 'string') return null;
  // Extract the part after underscore and uppercase it
  const parts = value.split('_');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : value.toUpperCase();
};


// Filter options - values match database field values
// Feature field determines card category (Ultra Hero, Kaiju, Scene, Ultra Mech)
const CARD_TYPE_OPTIONS = [
  { value: 'ultra_hero', label: 'Ultra Hero' },
  { value: 'kaiju', label: 'Kaiju' },
  { value: 'scene', label: 'Scene' },
  { value: 'ultra_mech', label: 'Ultra Mech' },
];

const LEVEL_OPTIONS = [
  { value: '1', label: 'Level 1' },
  { value: '2', label: 'Level 2' },
  { value: '3', label: 'Level 3' },
  { value: '4', label: 'Level 4' },
  { value: '5', label: 'Level 5' },
  { value: '6', label: 'Level 6' },
  { value: '7', label: 'Level 7' },
];

// Type field - card combat/attribute type
const TYPE_OPTIONS = [
  { value: 'basic', label: 'Basic' },
  { value: 'speed', label: 'Speed' },
  { value: 'power', label: 'Power' },
  { value: 'armed', label: 'Armed' },
  { value: 'devastation', label: 'Devastation' },
  { value: 'disaster', label: 'Hazard' },
  { value: 'meteo', label: 'Meteo' },
  { value: 'invasion', label: 'Invasion' },
];

const RARITY_OPTIONS = [
  { value: '900_c', label: 'C (Common)' },
  { value: '800_u', label: 'U (Uncommon)' },
  { value: '600_r', label: 'R (Rare)' },
  { value: '500_rr', label: 'RR (Double Rare)' },
  { value: '400_rrr', label: 'RRR (Triple Rare)' },
  { value: '390_rrrr', label: 'RRRR (Quadruple Rare)' },
];

// Special value for "All" options since SelectItem can't have empty string
const ALL_VALUE = '__all__';

// Create rarity friendly name map
const RARITY_FRIENDLY_NAMES = {};
RARITY_OPTIONS.forEach(option => {
  RARITY_FRIENDLY_NAMES[option.value] = option.label;
});

// Create feature friendly name map
const FEATURE_FRIENDLY_NAMES = {};
CARD_TYPE_OPTIONS.forEach(option => {
  FEATURE_FRIENDLY_NAMES[option.value] = option.label;
});

/**
 * CardGallery page - Browse and explore all cards with pagination
 */
export default function CardGallery() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language, t } = useLanguage();

  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [currentQuery, setCurrentQuery] = useState('');
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCards, setTotalCards] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  // AbortController ref to cancel stale requests
  const abortControllerRef = useRef(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCardType, setSelectedCardType] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedRarity, setSelectedRarity] = useState('');

  // View state
  const [viewMode, setViewMode] = useState('medium'); // 'large', 'medium', 'details'
  const [selectedCard, setSelectedCard] = useState(null);
  const [modalCardData, setModalCardData] = useState(null);
  const [modalLanguage, setModalLanguage] = useState('EN');
  const [modalLoading, setModalLoading] = useState(false);
  
  // Alternate versions state (Art Parallels, etc.)
  const [cardVersions, setCardVersions] = useState({ baseVersion: null, alternateVersions: [] });
  const [selectedVersion, setSelectedVersion] = useState(null); // null = base version

  // Calculate total pages
  const totalPages = Math.ceil(totalCards / CARDS_PER_PAGE);

  // Count active filters
  const activeFilterCount = [
    selectedCardType,
    selectedLevel,
    selectedType,
    selectedRarity,
  ].filter(Boolean).length;

  // Handle URL search parameter on mount
  useEffect(() => {
    const searchParam = searchParams.get('search');
    if (searchParam && searchParam.trim()) {
      setSearchInput(searchParam);
      setCurrentQuery(searchParam);
      setCurrentPage(1);
    }
  }, [searchParams]);

  // Fetch cards
  const fetchCards = useCallback(async (page = 1) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    setLoading(true);
    setError('');

    try {
      const offset = (page - 1) * CARDS_PER_PAGE;
      const response = await fetch('/api/search-cards', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: currentQuery || '*', // Use wildcard for empty search to get all cards
          offset,
          limit: CARDS_PER_PAGE,
          cardTypes: selectedCardType ? [selectedCardType] : undefined,
          levels: selectedLevel ? [selectedLevel] : undefined,
          types: selectedType ? [selectedType] : undefined,
          rarity: selectedRarity || undefined,
          lang: language,
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      
      // Only update state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setCards(data.results || []);
        setTotalCards(data.total || data.results?.length || 0);
        setCurrentPage(page);
      }
    } catch (err) {
      // Ignore abort errors - they're expected when canceling stale requests
      if (err.name === 'AbortError') {
        return;
      }
      console.error('Fetch error:', err);
      setError(t('gallery.fetchError') || 'Failed to load cards. Please try again.');
    } finally {
      // Only clear loading if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [currentQuery, selectedCardType, selectedLevel, selectedType, selectedRarity, language, t]);

  // Load cards on mount and when filters change
  useEffect(() => {
    fetchCards(1);
  }, [fetchCards]);

  // Handle search
  const handleSearch = () => {
    setCurrentQuery(searchInput);
    setCurrentPage(1);
    fetchCards(1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchCards(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedCardType('');
    setSelectedLevel('');
    setSelectedType('');
    setSelectedRarity('');
    setSearchInput('');
    setCurrentQuery('');
  };

  // Fetch card data for a specific language
  const fetchCardForLanguage = useCallback(async (cardId, lang) => {
    setModalLoading(true);
    try {
      const response = await fetch(`/api/cards/${cardId}?lang=${lang}`);
      if (!response.ok) throw new Error('Failed to fetch card');
      const data = await response.json();
      setModalCardData(data);
    } catch (err) {
      console.error('Error fetching card:', err);
    } finally {
      setModalLoading(false);
    }
  }, []);

  // Fetch alternate versions (Art Parallels, etc.)
  const fetchCardVersions = useCallback(async (cardId, lang) => {
    try {
      const response = await fetch(`/api/cards/${cardId}/versions?lang=${lang}`);
      if (!response.ok) throw new Error('Failed to fetch card versions');
      const data = await response.json();
      setCardVersions({
        baseVersion: data.baseVersion,
        alternateVersions: data.alternateVersions || []
      });
    } catch (err) {
      console.error('Error fetching card versions:', err);
      setCardVersions({ baseVersion: null, alternateVersions: [] });
    }
  }, []);

  // Handle card selection for modal
  const handleCardSelect = useCallback((card) => {
    setSelectedCard(card);
    setModalLanguage(language); // Start with current UI language
    setSelectedVersion(null); // Reset to base version
    fetchCardForLanguage(card.id, language);
    fetchCardVersions(card.id, language);
  }, [language, fetchCardForLanguage, fetchCardVersions]);

  // Handle version selection (Art Parallel, etc.)
  const handleVersionSelect = useCallback((version) => {
    setSelectedVersion(version);
    if (version) {
      // Fetch the alternate version's data
      fetchCardForLanguage(version.id, modalLanguage);
    } else if (selectedCard) {
      // Switch back to base version
      fetchCardForLanguage(selectedCard.id, modalLanguage);
    }
  }, [selectedCard, modalLanguage, fetchCardForLanguage]);

  // Cycle language in modal
  const cycleModalLanguage = useCallback((direction) => {
    const currentIndex = LANGUAGES.indexOf(modalLanguage);
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % LANGUAGES.length
      : (currentIndex - 1 + LANGUAGES.length) % LANGUAGES.length;
    const newLang = LANGUAGES[newIndex];
    setModalLanguage(newLang);
    const cardIdToFetch = selectedVersion ? selectedVersion.id : selectedCard?.id;
    if (cardIdToFetch) {
      fetchCardForLanguage(cardIdToFetch, newLang);
      // Also refresh versions for new language
      if (selectedCard) {
        fetchCardVersions(selectedCard.id, newLang);
      }
    }
  }, [modalLanguage, selectedCard, selectedVersion, fetchCardForLanguage, fetchCardVersions]);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setSelectedCard(null);
    setModalCardData(null);
    setCardVersions({ baseVersion: null, alternateVersions: [] });
    setSelectedVersion(null);
  }, []);

  // Add card to deck (navigate to deck builder with card)
  const addToDeck = (card) => {
    // Create a minimal deck code with just this card
    const deckData = {
      version: 2,
      timestamp: new Date().toISOString(),
      cards: [{ id: card.id, count: 1 }],
    };
    const deckCode = btoa(unescape(encodeURIComponent(JSON.stringify(deckData))));
    navigate(`/deck-builder?load=${encodeURIComponent(deckCode)}`);
  };

  // Get grid classes based on view mode
  const getGridClasses = () => {
    switch (viewMode) {
      case 'large':
        return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';
      case 'details':
        return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';
      default: // medium
        return 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-5';
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with Gradient */}
      <div className="bg-gradient-to-r from-[#3c618a] to-[#7a67ba] text-white flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-1">
            <Images className="h-7 w-7" />
            <h1 className="text-2xl font-bold">
              {t('gallery.title') || 'Card Gallery'}
            </h1>
          </div>
          <p className="text-white/80 max-w-2xl text-sm">
            {t('gallery.description') || 'Browse and explore the complete card database with advanced filtering and search.'}
          </p>
        </div>
      </div>

      {/* Search and Filters Section */}
      <div className="bg-white border-b px-4 py-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto">

          {/* Search and Filter Bar */}
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Input
                placeholder={t('gallery.searchPlaceholder') || 'Search cards...'}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pr-10"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-8 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setSearchInput('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={handleSearch}
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {/* Filter Toggle */}
            <Button
              variant={showFilters ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-1"
            >
              <Filter className="h-4 w-4" />
              {t('gallery.filters') || 'Filters'}
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>

            {/* View Mode Toggles */}
            <div className="flex items-center border rounded-md">
              <Toggle
                pressed={viewMode === 'details'}
                onPressedChange={() => setViewMode('details')}
                size="sm"
                className="rounded-r-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                title={t('gallery.listView') || 'List view'}
              >
                <List className="h-4 w-4" />
              </Toggle>
              <Toggle
                pressed={viewMode === 'large'}
                onPressedChange={() => setViewMode('large')}
                size="sm"
                className="rounded-none border-x data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                title={t('gallery.largeGrid') || 'Large grid'}
              >
                <LayoutGrid className="h-4 w-4" />
              </Toggle>
              <Toggle
                pressed={viewMode === 'medium'}
                onPressedChange={() => setViewMode('medium')}
                size="sm"
                className="rounded-l-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                title={t('gallery.mediumGrid') || 'Medium grid'}
              >
                <Grid3X3 className="h-4 w-4" />
              </Toggle>
            </div>

            {/* Results count */}
            <span className="text-sm text-muted-foreground ml-auto">
              {totalCards > 0 
                ? `${t('gallery.showing') || 'Showing'} ${(currentPage - 1) * CARDS_PER_PAGE + 1}-${Math.min(currentPage * CARDS_PER_PAGE, totalCards)} ${t('gallery.of') || 'of'} ${totalCards}`
                : ''}
            </span>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {/* Card Type Filter */}
                <Select 
                  value={selectedCardType || ALL_VALUE} 
                  onValueChange={(v) => setSelectedCardType(v === ALL_VALUE ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('gallery.cardType') || 'Card Type'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_VALUE}>All Types</SelectItem>
                    {CARD_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Level Filter */}
                <Select 
                  value={selectedLevel || ALL_VALUE} 
                  onValueChange={(v) => setSelectedLevel(v === ALL_VALUE ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('gallery.level') || 'Level'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_VALUE}>All Levels</SelectItem>
                    {LEVEL_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Type Filter */}
                <Select 
                  value={selectedType || ALL_VALUE} 
                  onValueChange={(v) => setSelectedType(v === ALL_VALUE ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('gallery.type') || 'Type'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_VALUE}>All Types</SelectItem>
                    {TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Rarity Filter */}
                <Select 
                  value={selectedRarity || ALL_VALUE} 
                  onValueChange={(v) => setSelectedRarity(v === ALL_VALUE ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('gallery.rarity') || 'Rarity'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_VALUE}>All Rarities</SelectItem>
                    {RARITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="mt-2 text-muted-foreground"
                >
                  <X className="h-4 w-4 mr-1" />
                  {t('gallery.clearFilters') || 'Clear all filters'}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Card Grid */}
      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={() => fetchCards(currentPage)}>
                {t('common.retry') || 'Retry'}
              </Button>
            </div>
          ) : cards.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <p>{t('gallery.noResults') || 'No cards found matching your criteria.'}</p>
            </div>
          ) : (
            <>
              {/* Cards Grid - 6 rows of 5 = 30 cards */}
              <div className={`grid gap-4 ${getGridClasses()}`}>
                {cards.map((card) => {
                  const normalized = normalizeCard(card);
                  const imageUrl = getProxyImageUrl(
                    normalized.images?.blob?.full || normalized.images?.original?.full
                  );
                  const isScene = normalized.feature === 'scene';

                  return (
                    <div
                      key={normalized.id || normalized.cardNumber}
                      className={`group relative bg-white rounded-lg shadow-sm border border-border overflow-hidden cursor-pointer transition-all hover:shadow-md hover:-translate-y-1 ${
                        isScene ? 'aspect-[305/220]' : 'aspect-[220/305]'
                      }`}
                      onClick={() => handleCardSelect(normalized)}
                    >
                      {imageUrl && (
                        <img
                          src={imageUrl}
                          alt={normalized.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                      <LanguageBadge 
                        cardLanguage={normalized.language} 
                        selectedLanguage={language} 
                      />

                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2">
                        <p className="text-white text-sm font-semibold text-center line-clamp-2 mb-1">
                          {normalized.name}
                        </p>
                        <p className="text-white/80 text-xs mb-2">
                          {normalized.cardNumber || normalized.number}
                        </p>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            addToDeck(normalized);
                          }}
                        >
                          <Plus className="h-4 w-4" />
                          {t('gallery.addToDeck') || 'Add to Deck'}
                        </Button>
                      </div>

                      {/* Details view shows info below image */}
                      {viewMode === 'details' && (
                        <div className="absolute bottom-0 left-0 right-0 bg-white/95 p-3">
                          <p className="font-semibold text-sm truncate mb-2">{normalized.name}</p>
                          <p className="text-xs text-muted-foreground mb-2">
                            {normalized.cardNumber || normalized.number}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {normalized.characterName && (
                              <Badge variant="secondary" className="text-xs">
                                {normalized.characterName}
                              </Badge>
                            )}
                            {normalized.feature && (
                              <Badge variant="outline" className="text-xs">
                                {FEATURE_FRIENDLY_NAMES[typeof normalized.feature === 'string' ? normalized.feature : normalized.feature.value] || 
                                 (typeof normalized.feature === 'string' ? normalized.feature : normalized.feature.value)}
                              </Badge>
                            )}
                            {normalized.rarity && (
                              <Badge variant="outline" className="text-xs">
                                {RARITY_FRIENDLY_NAMES[typeof normalized.rarity === 'string' ? normalized.rarity : normalized.rarity.value] || 
                                 (typeof normalized.rarity === 'string' ? normalized.rarity : normalized.rarity.value)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6 pb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t('gallery.previous') || 'Previous'}
                  </Button>

                  <div className="flex items-center gap-1">
                    {/* First page */}
                    {currentPage > 3 && (
                      <>
                        <Button
                          variant={currentPage === 1 ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => handlePageChange(1)}
                          className="w-9"
                        >
                          1
                        </Button>
                        {currentPage > 4 && <span className="px-2 text-muted-foreground">...</span>}
                      </>
                    )}

                    {/* Page numbers around current */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      if (pageNum < 1 || pageNum > totalPages) return null;
                      if (currentPage > 3 && pageNum === 1) return null;
                      if (currentPage < totalPages - 2 && pageNum === totalPages) return null;

                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          className="w-9"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}

                    {/* Last page */}
                    {currentPage < totalPages - 2 && totalPages > 5 && (
                      <>
                        {currentPage < totalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
                        <Button
                          variant={currentPage === totalPages ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => handlePageChange(totalPages)}
                          className="w-9"
                        >
                          {totalPages}
                        </Button>
                      </>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    {t('gallery.next') || 'Next'}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Card Detail Modal */}
      <Dialog open={!!selectedCard} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {modalCardData?.name || selectedCard?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedCard && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Card Image with Language Toggle */}
              <div className="relative flex flex-col items-center">
                <div className={`relative ${selectedCard.feature === 'scene' ? 'aspect-[305/220]' : 'aspect-[220/305]'} w-full max-w-[320px]`}>
                  {/* Previous Language Arrow */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-[-40px] top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 shadow hover:bg-white z-10"
                    onClick={() => cycleModalLanguage('prev')}
                    disabled={modalLoading}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>

                  {/* Card Image */}
                  <div className="relative w-full h-full">
                    {modalLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-10">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    )}
                    <img
                      src={getProxyImageUrl(
                        modalCardData?.images?.blob?.full || 
                        modalCardData?.images?.original?.full ||
                        selectedCard.images?.blob?.full || 
                        selectedCard.images?.original?.full
                      )}
                      alt={modalCardData?.name || selectedCard?.name}
                      className="w-full h-full object-contain rounded-lg shadow-lg"
                    />
                  </div>

                  {/* Next Language Arrow */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-[-40px] top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 shadow hover:bg-white z-10"
                    onClick={() => cycleModalLanguage('next')}
                    disabled={modalLoading}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                </div>

                {/* Language Indicator */}
                <div className="flex items-center gap-2 mt-3">
                  {LANGUAGES.map((lang) => (
                    <Badge
                      key={lang}
                      variant={modalLanguage === lang ? 'default' : 'outline'}
                      className={`cursor-pointer ${modalLanguage === lang ? '' : 'opacity-50'}`}
                      onClick={() => {
                        setModalLanguage(lang);
                        const cardIdToFetch = selectedVersion ? selectedVersion.id : selectedCard.id;
                        fetchCardForLanguage(cardIdToFetch, lang);
                        fetchCardVersions(selectedCard.id, lang);
                      }}
                    >
                      {SUPPORTED_LANGUAGES[lang]?.flag} {SUPPORTED_LANGUAGES[lang]?.name || lang}
                    </Badge>
                  ))}
                </div>

                {/* Alternate Versions (Art Parallel, etc.) */}
                {cardVersions.alternateVersions.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 text-center">
                      {t('gallery.versions') || 'Versions'}
                    </p>
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      {/* Base Version Thumbnail */}
                      {cardVersions.baseVersion && (
                        <div
                          className={`relative cursor-pointer rounded-md overflow-hidden border-2 transition-all ${
                            !selectedVersion ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-muted-foreground/50'
                          }`}
                          onClick={() => handleVersionSelect(null)}
                          title={`Base - ${formatRarity(cardVersions.baseVersion.rarity) || 'Regular'}`}
                        >
                          <img
                            src={getProxyImageUrl(
                              cardVersions.baseVersion.images?.blob?.thumbnail ||
                              cardVersions.baseVersion.images?.original?.thumbnail ||
                              cardVersions.baseVersion.images?.blob?.full ||
                              cardVersions.baseVersion.images?.original?.full
                            )}
                            alt="Base version"
                            className="w-12 h-16 object-cover"
                          />
                          <Badge className="absolute bottom-0 left-0 right-0 text-[8px] rounded-none justify-center py-0">
                            {formatRarity(cardVersions.baseVersion.rarity) || 'Base'}
                          </Badge>
                        </div>
                      )}

                      {/* Alternate Version Thumbnails */}
                      {cardVersions.alternateVersions.map((version) => (
                        <div
                          key={version.id}
                          className={`relative cursor-pointer rounded-md overflow-hidden border-2 transition-all ${
                            selectedVersion?.id === version.id ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-muted-foreground/50'
                          }`}
                          onClick={() => handleVersionSelect(version)}
                          title={`${version.branch || 'Alt'} - ${formatRarity(version.rarity) || 'Alternate'}`}
                        >
                          <img
                            src={getProxyImageUrl(
                              version.images?.blob?.thumbnail ||
                              version.images?.original?.thumbnail ||
                              version.images?.blob?.full ||
                              version.images?.original?.full
                            )}
                            alt={`${version.branch || 'Alternate'} version`}
                            className="w-12 h-16 object-cover"
                          />
                          <Badge variant="secondary" className="absolute bottom-0 left-0 right-0 text-[8px] rounded-none justify-center py-0 bg-amber-500 text-white">
                            {formatRarity(version.rarity) || version.branch || 'Alt'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Card Details */}
              <ScrollArea className="h-[60vh] pr-4">
                <div className="space-y-4">
                  {/* Card Name */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('gallery.cardName') || 'Card Name'}
                    </p>
                    <p className="text-lg font-semibold">
                      {modalCardData?.name || selectedCard?.name}
                    </p>
                  </div>

                  {/* Character Name */}
                  {(modalCardData?.characterName || selectedCard?.characterName) && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('gallery.characterName') || 'Character'}
                      </p>
                      <p className="font-medium">
                        {modalCardData?.characterName || selectedCard?.characterName}
                      </p>
                    </div>
                  )}

                  {/* Card Level */}
                  {(modalCardData?.level || selectedCard?.level) && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('gallery.level') || 'Level'}
                      </p>
                      <Badge variant="secondary" className="text-sm">
                        Lv. {modalCardData?.level || selectedCard?.level}
                      </Badge>
                    </div>
                  )}

                  {/* Card ID / Number */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('gallery.cardNumber') || 'Card ID'}
                    </p>
                    <p className="font-mono text-sm">
                      {modalCardData?.number || selectedCard?.cardNumber || selectedCard?.number}
                    </p>
                  </div>

                  {/* Card Type */}
                  {(modalCardData?.type || selectedCard?.type) && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('gallery.type') || 'Card Type'}
                      </p>
                      <p>
                        {typeof (modalCardData?.type || selectedCard?.type) === 'string' 
                          ? (modalCardData?.type || selectedCard?.type)
                          : (modalCardData?.type?.value || selectedCard?.type?.value)}
                      </p>
                    </div>
                  )}

                  {/* Card Effect */}
                  {(modalCardData?.effect || selectedCard?.effect) && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('gallery.effect') || 'Card Effect'}
                      </p>
                      <p className="text-sm leading-relaxed bg-muted/50 p-3 rounded-md">
                        {modalCardData?.effect || selectedCard?.effect}
                      </p>
                    </div>
                  )}

                  {/* Flavor Text */}
                  {(modalCardData?.flavorText || selectedCard?.flavorText) && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('gallery.flavorText') || 'Flavor Text'}
                      </p>
                      <p className="text-sm italic text-muted-foreground bg-muted/30 p-3 rounded-md">
                        "{modalCardData?.flavorText || selectedCard?.flavorText}"
                      </p>
                    </div>
                  )}

                  {/* Add to Deck Button */}
                  <Button 
                    onClick={() => addToDeck(modalCardData || selectedCard)} 
                    className="w-full mt-4"
                    size="lg"
                    disabled={!!selectedVersion || modalLanguage !== language}
                    title={selectedVersion ? 'Cannot add alternate art versions to deck' : modalLanguage !== language ? `Switch to ${language} to add to deck` : ''}
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    {t('gallery.addToDeck') || 'Add to Deck'}
                  </Button>
                </div>
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
